import { useQuery } from '@tanstack/react-query';

import { searchApi } from '@/services/search';
import { AutocompleteResponseV2 } from '@/types/search';

export const AUTOCOMPLETE_QUERY_KEY = 'autocomplete';

export interface UseAutocompleteOptions {
  q: string;
  types?: string;
  limit?: number;
  enabled?: boolean;
}

export const useAutocomplete = ({
  q,
  types,
  limit = 10,
  enabled = true,
}: UseAutocompleteOptions) => {
  const trimmedQuery = q.trim();
  const isQueryReady = trimmedQuery.length >= 2;

  const query = useQuery<AutocompleteResponseV2 | undefined>({
    queryKey: [AUTOCOMPLETE_QUERY_KEY, trimmedQuery, types ?? null, limit],
    queryFn: ({ signal }) =>
      searchApi.autocomplete(
        {
          q: trimmedQuery,
          limit,
          ...(types ? { types } : {}),
        },
        signal
      ),
    enabled: enabled && isQueryReady,
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      if (!previousKey) return undefined;
      const previousQueryText = previousKey[1];
      const previousTypes = previousKey[2];
      if (
        previousQueryText === trimmedQuery &&
        previousTypes === (types ?? null)
      ) {
        return previousData;
      }
      return undefined;
    },
  });

  return {
    data: query.data,
    groups: query.data?.groups,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
};
